import { Check, CheckCircle2, CircleDot, Eye, EyeOff, Loader2, MailCheck } from 'lucide-react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  checkSignUpPromoCode,
  getRegistration,
  getRegistrationConfig,
  sendSignUpEmailCode,
  startRegistrationCheckout,
  startSignUp,
  verifySignUpEmailCode
} from '../../api/registrationApi'
import { cardFeatures, cardTiers, formatPrice, introLimitNotice, tierById } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { validations } from '../../utils/utils'
import { PlanGrid } from '../billing/PlanGrid'
import { PromoCodeField, promoCovers } from '../billing/PromoCodeField'
import { PlanCard, centerRecommended } from './ChoosePlan'
import { Recaptcha, executeRecaptchaV3, loadRecaptchaV3 } from './Recaptcha'
import {
  ErrorBox,
  InfoBox,
  RegistrationShell,
  fieldClass,
  fieldErrorClass,
  labelClass,
  linkButtonClass,
  primaryButtonClass
} from './RegistrationShell'
import {
  EMPTY_SIGN_UP,
  FIELD_LIMITS,
  Field,
  FieldError,
  PASSWORD_RULES,
  StateSelect,
  US_PHONE_FORMAT,
  formatUsPhone,
  nextFieldValue,
  toRegistrationPayload,
  validateAccount,
  validateRegistration,
  validateShop
} from './registrationFields'
import { applySignUpSession } from './signUpSession'
import './signUpWizard.css'

const STEPS = ['Package', 'Your shop', 'Your account']
const CODE_LENGTH = 6
const NO_CODE = { status: 'idle', sentTo: null, message: null, error: null, resendAt: 0, token: null }
const sendButtonClass =
  'h-11 shrink-0 rounded-xl border border-white/25 bg-white/[0.08] px-4 text-[12px] font-semibold text-white transition-colors hover:bg-white/[0.14] disabled:opacity-60'

/** What the final button says for the chosen package (a promo code makes its days free). */
export const finishLabel = (tier, promo) => {
  if (!tier) return 'Create my account'
  if (promoCovers(promo, tier)) return `Start ${promo.freeDays} days free`
  return `Subscribe to ${tier.name}`
}

const StepHeader = ({ step }) => (
  <div className='mb-6' aria-label={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}`}>
    <ol className='flex items-center gap-2'>
      {STEPS.map((label, i) => (
        <li key={label} className='flex flex-1 items-center gap-2 last:flex-none'>
          <span
            aria-current={i === step ? 'step' : undefined}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold transition-all duration-300 ${
              i < step
                ? 'border-emerald-400/50 bg-emerald-500/15 text-emerald-300'
                : i === step
                  ? 'scale-110 border-[#1F6FEB] bg-[#1F6FEB] text-white'
                  : 'border-white/25 text-white/55'
            }`}
          >
            {i < step ? <Check className='h-3.5 w-3.5' aria-hidden='true' /> : i + 1}
          </span>
          <span className={`text-[12px] transition-colors duration-300 ${i === step ? 'font-semibold text-white' : 'text-white/55'}`}>
            {label}
          </span>
          {i < STEPS.length - 1 && <span className='hidden h-px flex-1 bg-white/15 sm:block' aria-hidden='true' />}
        </li>
      ))}
    </ol>
    <div className='mt-3 h-[3px] overflow-hidden rounded-full bg-white/10'>
      <div
        className='h-full rounded-full bg-[#1F6FEB] transition-[width] duration-500 ease-out'
        style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
      />
    </div>
  </div>
)
StepHeader.propTypes = { step: PropTypes.number.isRequired }

/** The chosen package, shown on the later steps, with a way back to change it. */
const ChoiceChip = ({ tier, promo, onChange }) => (
  <div className='signup-drop mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-[#3B82F6]/40 bg-[#1F6FEB]/15 px-4 py-2 text-[13px] text-white'>
    <span>
      {tier ? (
        <>
          <span className='font-semibold'>{tier.name}</span>
          {promoCovers(promo, tier) ? (
            <span className='text-emerald-300'> · free for {promo.freeDays} days</span>
          ) : (
            <span className='text-white/70'> · {formatPrice(tier.priceCents)} / location / mo</span>
          )}
        </>
      ) : (
        <span>No package yet — you&apos;ll choose one later</span>
      )}
    </span>
    <button type='button' onClick={onChange} className='text-[12px] font-semibold text-[#93C5FD] underline-offset-2 hover:underline'>
      Change
    </button>
  </div>
)
ChoiceChip.propTypes = { tier: PropTypes.object, promo: PropTypes.object, onChange: PropTypes.func.isRequired }

/**
 * /register — the one-page sign-up (2026-10-22): choose a package (the existing cards, promo code included), the
 * shop's details, then the owner's account. The login email is confirmed with a 6-digit code right in the form,
 * then Stripe Checkout opens; Stripe returns to /register/complete, which creates the account and signs the owner in
 * on Home. "Choose a plan later" creates the account at once instead (the app stays locked until a package is
 * bought) — its link is hidden for now. Also /register?checkout=cancel&token=… (and the older /register/plan?token=…):
 * a sign-up already staged goes straight back to choosing a package.
 */
export const SignUpWizard = () => {
  const history = useHistory()
  const { catalog, status: catalogStatus } = useCatalog()
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState('forward')
  // undefined = not chosen yet; null = "choose a plan later"; otherwise a tier id.
  const [tierId, setTierId] = useState(undefined)
  const [promo, setPromo] = useState(null)
  const [form, setForm] = useState(EMPTY_SIGN_UP)
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [code, setCode] = useState(NO_CODE)
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''))
  const [verifying, setVerifying] = useState(false)
  const [shake, setShake] = useState(0)
  const [clock, setClock] = useState(Date.now())
  const [trial, setTrial] = useState({ days: 30, feeCents: null, maxPerBuyer: null })
  const [captchaSiteKey, setCaptchaSiteKey] = useState(null)
  const [v3SiteKey, setV3SiteKey] = useState(null)
  const [captchaToken, setCaptchaToken] = useState(null)
  const [captchaError, setCaptchaError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  // A sign-up already staged (back from a cancelled Checkout, or an older email link): { token, status, company, reason }.
  const [resume, setResume] = useState(null)
  const captchaRef = useRef(null)
  const digitRefs = useRef([])
  const stepRef = useRef(null)

  const plans = centerRecommended(cardTiers(catalog))
  const tier = tierId ? tierById(catalog, tierId) : null
  const coveredPromo = promoCovers(promo, tier) ? promo.code : undefined

  useEffect(() => {
    let active = true
    getRegistrationConfig()
      .then((config) => {
        if (!active) return
        if (config?.trial_days != null) {
          setTrial({
            days: Number(config.trial_days),
            feeCents: config?.trial_fee_cents ?? null,
            maxPerBuyer: config?.intro_max_per_buyer ?? null
          })
        }
        if (!config?.captcha_site_key) return
        if (config.captcha_provider === 'recaptcha') setCaptchaSiteKey(config.captcha_site_key)
        if (config.captcha_provider === 'recaptcha_v3') {
          setV3SiteKey(config.captcha_site_key)
          // Load now: v3 scores the whole visit, and the code request then doesn't wait on the script.
          loadRecaptchaV3(config.captcha_site_key).catch(() => {})
        }
      })
      .catch(() => {
        // No config: no widget. If the backend does want a captcha, the code request says so.
      })
    return () => {
      active = false
    }
  }, [])

  // Back from a cancelled Checkout (or an older "choose a plan" link): the sign-up is staged — just pick a package.
  useEffect(() => {
    const { token, checkout } = qs.parse(window.location.search) || {}
    if (!token) return
    let active = true
    getRegistration(token)
      .then((view) => {
        if (!active) return
        setResume({ token, status: view?.status, company: view?.company_name, reason: checkout === 'cancel' ? 'cancel' : 'plan' })
      })
      .catch((err) => active && setSubmitError(getFriendlyErrorMessage(err)))
    return () => {
      active = false
    }
  }, [])

  // The resend countdown ticks only while there is one to show.
  useEffect(() => {
    if (code.status !== 'sent' || code.resendAt <= Date.now()) return undefined
    const timer = setInterval(() => setClock(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [code.status, code.resendAt])

  const goTo = (next) => {
    setDirection(next > step ? 'forward' : 'back')
    setStep(next)
    setSubmitError(null)
    stepRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }

  const resetCode = () => {
    setCode(NO_CODE)
    setDigits(Array(CODE_LENGTH).fill(''))
  }

  // ---------------------------------------------------------------- step 1: package

  const choose = async (id) => {
    setSubmitError(null)
    if (resume) {
      // Already staged: straight back to Stripe with the package now chosen.
      setSubmitting(true)
      try {
        const covered = promoCovers(promo, tierById(catalog, id)) ? promo.code : undefined
        const result = await startRegistrationCheckout(resume.token, id, covered)
        if (!result?.url) throw new Error('Checkout did not return a URL.')
        window.location.assign(result.url)
      } catch (err) {
        setSubmitting(false)
        setSubmitError(getFriendlyErrorMessage(err))
      }
      return
    }
    setTierId(id)
    goTo(1)
  }

  // ---------------------------------------------------------------- fields

  const onChange = (event) => {
    const { name } = event.target
    const value = nextFieldValue(name, event.target.value, form[name])
    const next = { ...form, [name]: value }
    setForm(next)
    // Once a field shows an error, re-check it as the user types so it clears the moment it's right.
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: validateRegistration(next)[name] }))
    if (name === 'password' && errors.confirm) setErrors((prev) => ({ ...prev, confirm: validateRegistration(next).confirm }))
    // A different email needs its own code.
    if (name === 'owner_email' && code.status !== 'idle' && value.trim().toLowerCase() !== code.sentTo) resetCode()
  }

  // Leaving a field checks it — but an untouched empty field waits for the step's Continue.
  const onBlur = (event) => {
    const { name, value } = event.target
    if (!value && !errors[name]) return
    setErrors((prev) => ({ ...prev, [name]: validateRegistration(form)[name] }))
  }

  const field = (name, label, props = {}) => (
    <Field
      name={name}
      label={label}
      value={form[name]}
      onChange={onChange}
      onBlur={onBlur}
      error={errors[name]}
      maxLength={FIELD_LIMITS[name]}
      {...props}
    />
  )

  const submitShop = (event) => {
    event.preventDefault()
    const found = validateShop(form)
    setErrors((prev) => ({ ...prev, ...found }))
    if (Object.keys(found).length === 0) goTo(2)
  }

  // ---------------------------------------------------------------- email code

  const sendCode = async () => {
    const email = form.owner_email.trim().toLowerCase()
    if (!validations.email(email) || email.length > FIELD_LIMITS.owner_email) {
      setErrors((prev) => ({ ...prev, owner_email: `Enter a valid email (${FIELD_LIMITS.owner_email} characters max)` }))
      return
    }
    if (captchaSiteKey && !captchaToken) {
      setCaptchaError(true)
      return
    }
    setCode((prev) => ({ ...prev, status: prev.status === 'sent' ? 'sent' : 'sending', error: null }))
    let token = captchaToken
    if (v3SiteKey) {
      try {
        token = await executeRecaptchaV3(v3SiteKey)
      } catch (err) {
        setCode((prev) => ({
          ...prev,
          status: prev.status === 'sending' ? 'idle' : prev.status,
          error: 'The security check couldn’t run. Check your connection or turn off content blockers, then try again.'
        }))
        return
      }
    }
    try {
      const result = await sendSignUpEmailCode(email, token || undefined)
      setDigits(Array(CODE_LENGTH).fill(''))
      // "Confirm your email first" no longer applies: the code is on its way.
      setErrors((prev) => ({ ...prev, owner_email: undefined }))
      setCode({
        status: 'sent',
        sentTo: email,
        message: result?.message || `We sent a 6-digit code to ${email}.`,
        error: null,
        resendAt: Date.now() + (result?.resend_after_seconds ?? 30) * 1000,
        token: null
      })
      setClock(Date.now())
      setTimeout(() => digitRefs.current[0]?.focus?.(), 50)
    } catch (err) {
      setCode((prev) => ({ ...prev, status: prev.status === 'sending' ? 'idle' : prev.status, error: getFriendlyErrorMessage(err) }))
    } finally {
      // A reCAPTCHA v2 token is single-use: the backend has consumed it either way.
      if (captchaSiteKey) {
        captchaRef.current?.reset?.()
        setCaptchaToken(null)
      }
    }
  }

  const verify = async (entered) => {
    setVerifying(true)
    try {
      const result = await verifySignUpEmailCode(code.sentTo, entered)
      if (result?.verified) {
        setCode((prev) => ({ ...prev, status: 'verified', token: result.email_token, error: null }))
        setErrors((prev) => ({ ...prev, owner_email: undefined }))
      } else {
        setCode((prev) => ({ ...prev, error: result?.message || 'That code isn’t right.' }))
        setDigits(Array(CODE_LENGTH).fill(''))
        setShake((n) => n + 1)
        setTimeout(() => digitRefs.current[0]?.focus?.(), 50)
      }
    } catch (err) {
      setCode((prev) => ({ ...prev, error: getFriendlyErrorMessage(err) }))
    } finally {
      setVerifying(false)
    }
  }

  const setDigit = (index, raw) => {
    let typed = String(raw).replace(/\D/g, '')
    // Typed over a filled box (its text wasn't selected): keep the new digit only.
    if (digits[index] && typed.length === 2 && typed.startsWith(digits[index])) typed = typed.slice(1)
    const next = [...digits]
    if (typed.length > 1) {
      // A paste (or autofill of the whole code) fills the boxes from here on.
      typed
        .slice(0, CODE_LENGTH - index)
        .split('')
        .forEach((d, k) => {
          next[index + k] = d
        })
    } else {
      next[index] = typed
    }
    setDigits(next)
    if (code.error) setCode((prev) => ({ ...prev, error: null }))
    const filled = next.join('')
    if (filled.length === CODE_LENGTH) {
      verify(filled)
    } else if (typed) {
      digitRefs.current[Math.min(index + typed.length, CODE_LENGTH - 1)]?.focus?.()
    }
  }

  const onDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) digitRefs.current[index - 1]?.focus?.()
  }

  // ---------------------------------------------------------------- finish

  const finish = async (event) => {
    event.preventDefault()
    const shopErrors = validateShop(form)
    if (Object.keys(shopErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...shopErrors }))
      goTo(1)
      return
    }
    const found = validateAccount(form)
    if (code.status !== 'verified' && !found.owner_email) {
      found.owner_email =
        code.status === 'sent' ? 'Enter the 6-digit code we sent to confirm your email.' : 'Confirm your email first — press Send code.'
    }
    setErrors((prev) => ({ ...prev, ...found }))
    if (Object.keys(found).length > 0) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const payload = toRegistrationPayload(form)
      const result = await startSignUp({ details: payload, tier: tierId, promoCode: coveredPromo, emailToken: code.token })
      if (result?.checkout_url) {
        window.location.assign(result.checkout_url)
        return
      }
      await applySignUpSession(result?.auth, payload.owner_email)
      history.replace('/')
    } catch (err) {
      const message = getFriendlyErrorMessage(err)
      setSubmitError(message)
      if (/confirmation has expired|Confirm your email/i.test(message)) resetCode()
      setSubmitting(false)
    }
  }

  // ---------------------------------------------------------------- render

  if (resume?.status === 'PROVISIONED') {
    return (
      <RegistrationShell width='narrow' title='Your account is ready'>
        <InfoBox>{resume.company || 'Your shop'} is already set up. Sign in to continue.</InfoBox>
        <button type='button' onClick={() => history.push('/authenticate')} className={`${primaryButtonClass} mt-5`}>
          Sign in
        </button>
      </RegistrationShell>
    )
  }

  const resendIn = Math.max(0, Math.ceil((code.resendAt - clock) / 1000))
  const animation = direction === 'forward' ? 'signup-step-forward' : 'signup-step-back'

  return (
    <RegistrationShell
      width={step === 0 ? 'plans' : 'wide'}
      title='Create your ADAS Intelligence account'
      subtitle={
        trial.days > 0
          ? `Every plan starts at ${trial.feeCents ? formatPrice(trial.feeCents) : 'no charge'} for the first ${trial.days} days — pick the one that fits your shop.`
          : 'Set up ADAS Intelligence for your calibration center.'
      }
    >
      <div ref={stepRef}>{!resume && <StepHeader step={step} />}</div>

      <div key={step} className={animation}>
        {step === 0 && (
          <div>
            {resume && (
              <div className='mb-5'>
                <InfoBox>
                  {resume.reason === 'cancel'
                    ? `Checkout was cancelled — nothing was charged. Choose a package to finish setting up ${resume.company || 'your shop'}.`
                    : `Your email is confirmed. Choose a package to finish setting up ${resume.company || 'your shop'}.`}
                </InfoBox>
              </div>
            )}
            {submitError && (
              <div className='mb-5'>
                <ErrorBox>{submitError}</ErrorBox>
              </div>
            )}
            {trial.days > 0 && introLimitNotice(trial.feeCents, trial.maxPerBuyer) && (
              <div className='mb-5'>
                <InfoBox>{introLimitNotice(trial.feeCents, trial.maxPerBuyer)}</InfoBox>
              </div>
            )}
            {catalogStatus !== 'ready' && plans.length === 0 ? (
              <div className='flex justify-center py-10' aria-busy='true'>
                <Loader2 className='h-6 w-6 animate-spin text-white/70' />
              </div>
            ) : (
              <PlanGrid count={plans.length} className='md:py-4'>
                {plans.map((t) => (
                  <PlanCard
                    key={t.id}
                    tier={t}
                    features={cardFeatures(t)}
                    pending={submitting}
                    onStart={choose}
                    promoDays={promoCovers(promo, t) ? promo.freeDays : undefined}
                  />
                ))}
              </PlanGrid>
            )}
            <div className='mt-6 flex justify-center'>
              <div className='w-full max-w-[520px]'>
                <PromoCodeField
                  applied={promo}
                  onApplied={setPromo}
                  onCheck={(entered) => checkSignUpPromoCode(entered)}
                  tone='dark'
                  align='center'
                  disabled={submitting}
                />
              </div>
            </div>
            <p className='mt-5 text-center text-[12px] text-white/45'>Payments are processed securely by Stripe. Prices in USD.</p>
            <div className='mt-3 flex flex-col items-center gap-2'>
              {/* Hidden for now (2026-10-22). The "choose a plan later" path still works end to end (tier null → the
                  account is created at once, locked until a package is bought); put the link back to offer it again.
              {!resume && (
                <button type='button' onClick={() => choose(null)} className={linkButtonClass}>
                  Not ready to buy? Create my account and choose a plan later
                </button>
              )}
              */}
              <button type='button' onClick={() => history.push('/authenticate')} className={linkButtonClass}>
                Already have an account? Sign in
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={submitShop} noValidate className='mx-auto max-w-[640px] space-y-3'>
            <ChoiceChip tier={tier} promo={promo} onChange={() => goTo(0)} />
            <h2 className='text-[15px] font-bold text-white/90'>Your shop</h2>
            {field('company_name', 'Shop name', { showCount: true, required: true, autoComplete: 'organization' })}
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              {field('email', 'Shop email', { type: 'email', required: true, autoComplete: 'email' })}
              {field('phone', 'Shop phone', {
                type: 'tel',
                required: true,
                value: formatUsPhone(form.phone),
                maxLength: US_PHONE_FORMAT.length,
                autoComplete: 'tel',
                placeholder: '(303) 555-0142',
                inputMode: 'numeric'
              })}
            </div>
            {field('shop_address', 'Address', {
              showCount: true,
              required: true,
              autoComplete: 'street-address',
              placeholder: '1420 Wadsworth Blvd, Suite 200'
            })}
            <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
              <div className='col-span-2 sm:col-span-1'>
                {field('shop_city', 'City', { showCount: true, required: true, autoComplete: 'address-level2' })}
              </div>
              <StateSelect value={form.shop_state} onChange={onChange} onBlur={onBlur} error={errors.shop_state} />
              {field('shop_zip', 'ZIP', { required: true, autoComplete: 'postal-code', inputMode: 'numeric', placeholder: '80214' })}
            </div>
            <p className='text-[11px] text-white/45'>Available for shops in the United States.</p>
            <div className='flex items-center justify-between gap-3 pt-3'>
              <button type='button' onClick={() => goTo(0)} className={linkButtonClass}>
                ← Back
              </button>
              <button type='submit' className={`${primaryButtonClass} max-w-[220px]`}>
                Continue
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={finish} noValidate className='mx-auto max-w-[640px] space-y-3'>
            <ChoiceChip tier={tier} promo={promo} onChange={() => goTo(0)} />
            <h2 className='text-[15px] font-bold text-white/90'>Your account</h2>

            <div>
              <label htmlFor='reg-owner_email' className={labelClass}>
                Email (your login)<span className='text-white/40'> *</span>
              </label>
              <div className='flex gap-2'>
                <input
                  id='reg-owner_email'
                  name='owner_email'
                  type='email'
                  value={form.owner_email}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxLength={FIELD_LIMITS.owner_email}
                  autoComplete='email'
                  aria-invalid={Boolean(errors.owner_email)}
                  aria-describedby={errors.owner_email ? 'reg-owner_email-error' : undefined}
                  className={`${fieldClass} ${errors.owner_email ? fieldErrorClass : ''} ${
                    code.status === 'verified' ? 'border-emerald-400/60' : ''
                  }`}
                />
                {code.status !== 'verified' && (
                  <button type='button' onClick={sendCode} disabled={code.status === 'sending' || resendIn > 0} className={sendButtonClass}>
                    {code.status === 'sending' ? (
                      <Loader2 className='h-4 w-4 animate-spin' aria-label='Sending' />
                    ) : code.status === 'sent' ? (
                      resendIn > 0 ? (
                        `Resend in ${resendIn}s`
                      ) : (
                        'Resend code'
                      )
                    ) : (
                      'Send code'
                    )}
                  </button>
                )}
              </div>
              <FieldError id='reg-owner_email-error' error={errors.owner_email} />
              {code.status === 'verified' && (
                <p className='signup-drop mt-1.5 flex items-center gap-1.5 pl-1 text-[12px] text-emerald-300' role='status'>
                  <MailCheck className='h-4 w-4' aria-hidden='true' />
                  Email confirmed
                </p>
              )}
              {code.status !== 'verified' && code.status !== 'sent' && !code.error && (
                <p className='mt-1 pl-1 text-[11px] text-white/45'>We&apos;ll email you a 6-digit code to confirm it before payment.</p>
              )}
              {captchaSiteKey && code.status !== 'verified' && (
                <div className='mt-2'>
                  <Recaptcha
                    ref={captchaRef}
                    siteKey={captchaSiteKey}
                    onChange={(t) => {
                      setCaptchaToken(t)
                      if (t) setCaptchaError(false)
                    }}
                  />
                  {captchaError && <div className='mt-1 text-center text-[11px] text-red-300'>Confirm you&apos;re not a robot</div>}
                </div>
              )}
              {code.status === 'sent' && (
                <div className='signup-drop mt-3 rounded-xl border border-white/10 bg-white/[0.05] p-4'>
                  <p className='text-[12px] text-white/70'>{code.message}</p>
                  <div key={shake} role='group' aria-label='6-digit code' className={`mt-3 flex gap-2 ${shake ? 'signup-shake' : ''}`}>
                    {digits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => {
                          digitRefs.current[i] = el
                        }}
                        value={d}
                        onChange={(e) => setDigit(i, e.target.value)}
                        onKeyDown={(e) => onDigitKeyDown(i, e)}
                        onPaste={(e) => {
                          e.preventDefault()
                          setDigit(i, e.clipboardData.getData('text'))
                        }}
                        onFocus={(e) => e.target.select?.()}
                        inputMode='numeric'
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={CODE_LENGTH}
                        disabled={verifying}
                        aria-label={`Digit ${i + 1}`}
                        className='h-12 w-11 rounded-xl border border-white/20 bg-white/[0.10] text-center text-[20px] font-semibold text-white outline-none transition-colors focus:border-[#3B82F6] disabled:opacity-60'
                      />
                    ))}
                    {verifying && <Loader2 className='ml-1 h-5 w-5 self-center animate-spin text-white/70' aria-label='Checking' />}
                  </div>
                  <p className='mt-2 text-[11px] text-white/45'>Didn&apos;t get it? Check spam, or resend when the timer ends.</p>
                </div>
              )}
              {code.error && (
                <div className='mt-1 pl-1 text-[11px] text-red-300' role='alert'>
                  {code.error}
                </div>
              )}
            </div>

            {field('owner_name', 'Full name', { required: true, autoComplete: 'name' })}
            {field('owner_phone', 'Mobile', {
              type: 'tel',
              required: true,
              value: formatUsPhone(form.owner_phone),
              maxLength: US_PHONE_FORMAT.length,
              autoComplete: 'tel',
              placeholder: '(303) 555-0142',
              inputMode: 'numeric'
            })}
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <div className='relative'>
                {field('password', 'Password', {
                  type: showPassword ? 'text' : 'password',
                  maxLength: 22,
                  required: true,
                  autoComplete: 'new-password'
                })}
                <button
                  type='button'
                  onClick={() => setShowPassword((s) => !s)}
                  className='absolute right-3 top-[30px] text-white/50 transition-colors hover:text-white/80'
                  aria-label='Toggle password visibility'
                >
                  {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                </button>
              </div>
              {field('confirm', 'Confirm password', {
                type: showPassword ? 'text' : 'password',
                maxLength: 22,
                required: true,
                autoComplete: 'new-password'
              })}
            </div>
            <ul className='grid grid-cols-1 gap-1 sm:grid-cols-2' aria-label='Password requirements'>
              {PASSWORD_RULES.map((rule) => {
                const met = rule.test(form.password)
                return (
                  <li key={rule.key} className={`flex items-center gap-2 text-[11px] ${met ? 'text-emerald-300' : 'text-white/45'}`}>
                    {met ? <CheckCircle2 className='h-3.5 w-3.5' /> : <CircleDot className='h-3.5 w-3.5' />}
                    {rule.label}
                  </li>
                )
              })}
            </ul>

            {submitError && <ErrorBox>{submitError}</ErrorBox>}

            <div className='flex items-center justify-between gap-3 pt-3'>
              <button
                type='button'
                onClick={() => goTo(1)}
                className={`${linkButtonClass} shrink-0 whitespace-nowrap`}
                disabled={submitting}
              >
                ← Back
              </button>
              <button
                type='submit'
                disabled={submitting}
                className={`${primaryButtonClass} sm:w-auto sm:min-w-[260px] sm:px-8 ${code.status === 'verified' ? '' : 'opacity-70'}`}
              >
                {submitting ? <Loader2 className='h-5 w-5 animate-spin' /> : `${finishLabel(tier, promo)}${tier ? ' →' : ''}`}
              </button>
            </div>
            {tier && <p className='text-right text-[11px] text-white/45'>Next: secure payment with Stripe.</p>}
            {v3SiteKey && (
              <p className='text-center text-[10px] text-white/35'>
                Protected by reCAPTCHA — Google&apos;s{' '}
                <a href='https://policies.google.com/privacy' target='_blank' rel='noreferrer' className='underline'>
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href='https://policies.google.com/terms' target='_blank' rel='noreferrer' className='underline'>
                  Terms
                </a>{' '}
                apply.
              </p>
            )}
          </form>
        )}
      </div>
    </RegistrationShell>
  )
}
